// The store's default English language uses the root URL, not /en/.
window.fetchPredictiveSearchHTML = async function (input) {
    const url = new URL(input, window.location.origin);
    let response = await fetch(url, { headers: { Accept: 'text/html' } });
    let usedEnglish = false;
    if (response.status === 417 && url.pathname !== '/search/suggest') {
        const englishUrl = new URL(url);
        englishUrl.pathname = '/search/suggest';
        response = await fetch(englishUrl, { headers: { Accept: 'text/html' } });
        usedEnglish = true;
    }
    if (!response.ok) {
        const error = new Error(`Search request failed: ${response.status}`);
        error.status = response.status;
        throw error;
    }
    const html = await response.text();
    if (!usedEnglish) return html;

    // Keep the visitor's language on navigation, including cards inside merge templates.
    const localeRoot = url.pathname.slice(0, -'search/suggest'.length);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const localizeLinks = root => {
        root.querySelectorAll('a[href]').forEach(link => {
            const target = new URL(link.getAttribute('href'), window.location.origin);
            if (target.origin !== window.location.origin) return;
            if (!/^\/(products|collections|blogs|pages|search)(\/|$)/.test(target.pathname)) return;
            target.pathname = localeRoot + target.pathname.slice(1);
            link.setAttribute('href', target.pathname + target.search + target.hash);
        });
        root.querySelectorAll('template').forEach(template => localizeLinks(template.content));
    };
    localizeLinks(doc);
    return doc.documentElement.outerHTML;
};

class PredictiveSearch extends HTMLElement {
    constructor() {
        super();
        this.states = {
            OPEN: 'predictive-search-open',
            LOADING: 'btn--loading',
            SEARCH_OPEN: 'search-open',
        };
        this.cachedMap = new Map();
        this.blogSearchCache = new Map();
        this.requestSequence = 0;
        this.focusElement = this.input;
        this.form = this.querySelector('form');
        // 서버에서 HTML 변환된 표시값 대신 URL의 원래 검색어로 입력값을 복원합니다.
        if (this.closest('.template-search')) {
            const query = new URL(window.location.href).searchParams.get('q');
            if (query !== null) this.input.value = query;
        }
        this.submissionQueryInput = this.createSubmissionQueryInput();
        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.form?.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', () => {
            this.cancelPendingRequest();
            // 빈 검색어는 추천 영역으로 즉시 복원하고, 캐시된 결과도 대기 없이 표시합니다.
            if (!this.getQuery() || this.cachedMap.has(this.setupURL().toString())) {
                this.onChange();
                return;
            }
            // 패널은 즉시 열되 연속 입력 중 서버 요청은 마지막 입력에서 150ms 뒤에 보냅니다.
            this.showPendingResults();
            this.searchRequestTimer = setTimeout(() => this.onChange(), 150);
        });
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.input.addEventListener('blur', this.onBlur.bind(this));
        this.searchContent = this.querySelector('.search__content');
        this.categorySearchIndex = this.parseCategorySearchIndex();
        this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
        this.header = document.querySelector('header');
        this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

        this.boundHandleClickOutside = this.handleClickOutside.bind(this);
        this.boundHandleMenuDrawerOpen = this.handleMenuDrawerOpen.bind(this);
        this.boundUpdateMobileSearchHeight = this.updateMobileSearchHeight.bind(this);
        this.searchProductTypes?.addEventListener(
            'change',
            this.handleProductTypeChange.bind(this)
        );
        this.searchVendors?.addEventListener('change', this.handleVendorChange.bind(this));
        this.initializeVendorFilter();
        this.initializeCustomVendorDropdown();

        this.isHeaderSearch = this.closest('header') !== null;
        this.isInPageSearch = this.closest('.template-search') !== null;
    }

    connectedCallback() {
        if (this.globalListenersAttached) return;

        document.addEventListener('click', this.boundHandleClickOutside);
        document.addEventListener('menu-drawer:open', this.boundHandleMenuDrawerOpen);
        window.addEventListener('resize', this.boundUpdateMobileSearchHeight);
        window.visualViewport?.addEventListener('resize', this.boundUpdateMobileSearchHeight);
        window.visualViewport?.addEventListener('scroll', this.boundUpdateMobileSearchHeight);
        this.globalListenersAttached = true;
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.boundHandleClickOutside);
        document.removeEventListener('menu-drawer:open', this.boundHandleMenuDrawerOpen);
        window.removeEventListener('resize', this.boundUpdateMobileSearchHeight);
        window.visualViewport?.removeEventListener('resize', this.boundUpdateMobileSearchHeight);
        window.visualViewport?.removeEventListener('scroll', this.boundUpdateMobileSearchHeight);
        this.globalListenersAttached = false;
        this.cancelPendingRequest();

        this.setInPageSearchState(false);
    }

    updateMobileSearchHeight() {
        if (!this.searchContent || window.innerWidth >= 1024) return;
        if (!this.classList.contains(this.states.OPEN)) return;

        const viewport = window.visualViewport;
        const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
        const panelTop = this.searchContent.getBoundingClientRect().top;
        this.searchContent.style.setProperty(
            '--mobile-search-available-height',
            `${Math.max(0, Math.floor(viewportBottom - panelTop))}px`
        );
    }

    handleMenuDrawerOpen() {
        this.toggleSearchState(false);
    }

    get input() {
        return this.querySelector('input[type="search"]');
    }
    get resetButton() {
        return this.querySelector('button[type="reset"]');
    }

    get searchProductTypes() {
        return this.querySelector('#SearchProductTypes');
    }

    get searchVendors() {
        return this.querySelector('[data-search-vendor-filter]');
    }

    get availableVendors() {
        if (this._availableVendors) return this._availableVendors;

        try {
            const vendors = JSON.parse(this.dataset.searchVendors || '[]');
            this._availableVendors = Array.isArray(vendors) ? vendors : [];
        } catch (error) {
            this._availableVendors = [];
        }

        return this._availableVendors;
    }

    normalizeSearchText(value) {
        return String(value || '')
            .normalize('NFKC')
            .toLocaleLowerCase()
            .replace(/[^\p{Letter}\p{Number}]+/gu, ' ')
            .trim()
            .replace(/\s+/g, ' ');
    }

    parseCategorySearchIndex() {
        const indexElement = this.querySelector('[data-category-search-index]');
        if (!indexElement) return [];

        try {
            const parsedIndex = JSON.parse(indexElement.textContent || '[]');
            if (!Array.isArray(parsedIndex)) return [];

            const seenUrls = new Set();

            return parsedIndex
                .filter(item => {
                    if (!item?.title || !item.url || seenUrls.has(item.url)) return false;

                    seenUrls.add(item.url);
                    return true;
                })
                .map((item, index) => {
                    const title = String(item.title).trim();
                    const description = String(item.description || '').trim();
                    const configuredOrder = Number(item.order);

                    return {
                        title,
                        description,
                        url: String(item.url),
                        order: Number.isFinite(configuredOrder) ? configuredOrder : index,
                        normalizedTitle: this.normalizeSearchText(title),
                        normalizedDescription: this.normalizeSearchText(description),
                    };
                });
        } catch (error) {
            console.error('Unable to parse the category search index.', error);
            return [];
        }
    }

    getCategorySearchMatches(query, limit = 3) {
        const normalizedQuery = this.normalizeSearchText(query);
        if (!normalizedQuery || this.categorySearchIndex.length === 0) return [];

        const queryTerms = normalizedQuery.split(' ');

        return this.categorySearchIndex
            .map(category => {
                const { normalizedTitle, normalizedDescription } = category;
                const combinedText = `${normalizedTitle} ${normalizedDescription}`.trim();

                if (!queryTerms.every(term => combinedText.includes(term))) return null;

                const titleWords = normalizedTitle.split(' ');
                let score = 0;

                if (normalizedTitle === normalizedQuery) {
                    score += 10000;
                } else if (normalizedTitle.startsWith(normalizedQuery)) {
                    score += 8000;
                } else if (normalizedTitle.includes(normalizedQuery)) {
                    score += 6000;
                } else if (normalizedDescription.includes(normalizedQuery)) {
                    score += 3000;
                }

                queryTerms.forEach(term => {
                    if (titleWords.includes(term)) {
                        score += 500;
                    } else if (titleWords.some(word => word.startsWith(term))) {
                        score += 300;
                    } else if (normalizedTitle.includes(term)) {
                        score += 200;
                    } else if (normalizedDescription.includes(term)) {
                        score += 50;
                    }
                });

                return { ...category, score };
            })
            .filter(Boolean)
            .sort(
                (first, second) =>
                    second.score - first.score ||
                    first.order - second.order ||
                    first.title.localeCompare(second.title)
            )
            .slice(0, limit);
    }

    createCategoryResultsSection(categories) {
        const sectionId = this.getSectionId();
        const categorySection = document.createElement('div');
        const heading = document.createElement('h4');
        const list = document.createElement('ul');

        categorySection.className =
            'grid gap-3 predictive-search-result search__box-item predictive-search-result--categories';
        categorySection.dataset.categorySearchResults = '';

        heading.id = `predictive-search-category-heading-${sectionId}`;
        heading.className = 'h6 predictive-search-result__heading';
        heading.textContent = 'Category';

        list.id = `predictive-search-results-category-list-${sectionId}`;
        list.className = 'predictive-search-result__list flex flex-col gap-y-2 text-subtext';
        list.setAttribute('role', 'group');
        list.setAttribute('aria-labelledby', heading.id);

        categories.forEach((category, index) => {
            const item = document.createElement('li');
            const link = document.createElement('a');

            item.id = `predictive-search-option-category-${sectionId}-${index + 1}`;
            item.className = 'predictive-search__list-item flex';
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', 'false');

            link.href = category.url;
            link.className = 'reversed-link';
            link.textContent = category.title;

            item.appendChild(link);
            list.appendChild(item);
        });

        categorySection.append(heading, list);
        return categorySection;
    }

    async loadBrandSearchIndex() {
        // 캐시된 헤더 HTML에 새 data 속성이 없어도 모든 퀵서치에서 인덱스를 읽습니다.
        const indexUrl =
            this.dataset.brandSearchIndexUrl ||
            `${window.Shopify?.routes?.root || '/'}?section_id=brand-search-index`;

        PredictiveSearch.brandSearchIndexes ??= new Map();
        if (!PredictiveSearch.brandSearchIndexes.has(indexUrl)) {
            const promise = (async () => {
                const brands = new Map();
                const visited = new Set();
                let url = new URL(indexUrl, window.location.origin);

                while (url) {
                    url.searchParams.set('section_id', 'brand-search-index');
                    if (visited.has(url.href)) throw new Error('Repeated brand index page');
                    visited.add(url.href);
                    const response = await fetch(url);
                    if (!response.ok) throw new Error('Unable to fetch brand search index');
                    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
                    const element = doc.querySelector('[data-brand-search-index]');
                    if (!element) throw new Error('Missing brand search index');
                    const page = JSON.parse(element.textContent);
                    if (!Array.isArray(page.brands)) throw new Error('Invalid brand search index');
                    page.brands.forEach(brand => {
                        if (!brand.title || !brand.url) return;
                        brands.set(brand.url, {
                            ...brand,
                            normalizedNames: [
                                brand.title,
                                brand.collectionTitle,
                                ...(brand.vendors || []),
                            ]
                                .map(name => this.normalizeSearchText(name))
                                .filter(Boolean),
                            normalizedDescription: this.normalizeSearchText(brand.description),
                        });
                    });
                    url = page.next ? new URL(page.next, url) : null;
                }
                return [...brands.values()];
            })();
            PredictiveSearch.brandSearchIndexes.set(indexUrl, promise);
        }

        try {
            this.brandSearchIndex = await PredictiveSearch.brandSearchIndexes.get(indexUrl);
        } catch (error) {
            PredictiveSearch.brandSearchIndexes.delete(indexUrl);
            throw error;
        }
    }

    getBrandSearchMatches(query, limit = 2) {
        const normalizedQuery = this.normalizeSearchText(query);
        if (!normalizedQuery) return [];
        const terms = normalizedQuery.split(' ');

        return (this.brandSearchIndex || [])
            .map(brand => {
                const names = brand.normalizedNames;
                const combined = [...names, brand.normalizedDescription].join(' ');
                if (!terms.every(term => combined.includes(term))) return null;

                const score =
                    Math.max(
                        ...names.map(name => {
                            if (name === normalizedQuery) return 10000;
                            if (name.startsWith(normalizedQuery)) return 8000;
                            if (name.includes(normalizedQuery)) return 6000;
                            return 0;
                        }),
                        brand.normalizedDescription.includes(normalizedQuery) ? 3000 : 0
                    ) +
                    terms.filter(term => names.some(name => name.includes(term))).length * 100;
                return { ...brand, score };
            })
            .filter(Boolean)
            .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
            .slice(0, limit);
    }

    renderBrandSearchResults(targetElement) {
        if (!this.brandSearchIndex) return false;
        const brands = this.getBrandSearchMatches(this.getQuery());
        if (!brands.length) return false;
        targetElement.querySelector('.predictive-search-result--brands')?.remove();

        // Category 결과와 동일한 보조 열을 사용하며, 설명만 일치해도 빈 상태를 대체합니다.
        const sideRail = this.ensureSearchSideRail(targetElement);
        const section = this.createCategoryResultsSection(brands);
        delete section.dataset.categorySearchResults;
        section.className =
            'grid gap-3 predictive-search-result search__box-item predictive-search-result--brands';
        const heading = section.querySelector('h4');
        heading.id = `predictive-search-brand-heading-${this.getSectionId()}`;
        heading.textContent = 'Brands';
        const list = section.querySelector('ul');
        list.id = `predictive-search-results-brand-list-${this.getSectionId()}`;
        list.className = 'predictive-search-result__media-list flex flex-col';
        list.setAttribute('aria-labelledby', heading.id);
        [...list.children].forEach((item, index) => {
            const brand = brands[index];
            item.id = `predictive-search-option-brand-${this.getSectionId()}-${index + 1}`;
            item.className = 'predictive-search-result__media-item';
            const link = item.querySelector('a');
            link.className = 'predictive-search-result__media-link';
            link.setAttribute('aria-label', brand.title);
            link.replaceChildren();
            const media = document.createElement('span');
            media.className = 'predictive-search-result__media';
            if (brand.image) {
                const image = document.createElement('img');
                image.src = brand.image;
                image.alt = brand.title;
                image.loading = 'lazy';
                image.className = 'predictive-search-result__media-image';
                media.appendChild(image);
            }
            const content = document.createElement('span');
            content.className = 'predictive-search-result__media-content';
            const title = document.createElement('span');
            title.className = 'predictive-search-result__media-title';
            title.textContent = brand.title;
            content.appendChild(title);
            if (brand.description) {
                const description = document.createElement('span');
                description.className = 'predictive-search-result__media-description';
                const words = brand.description.trim().split(/\s+/);
                description.textContent =
                    words.slice(0, 18).join(' ') + (words.length > 18 ? '...' : '');
                content.appendChild(description);
            }
            link.append(media, content);
        });
        const blog = sideRail.querySelector('.predictive-search-result--blog');
        sideRail.insertBefore(section, blog);
        return true;
    }

    ensureSearchSideRail(targetElement) {
        let layout = targetElement.querySelector('.predictive-search-results-layout');
        let sideRail = layout?.querySelector('.search__box-others');

        if (!layout) {
            targetElement.replaceChildren();
            layout = document.createElement('div');
            layout.className =
                'predictive-search-results-layout flex w-full flex-col gap-y-6 md:flex-row';
            targetElement.appendChild(layout);
        }
        if (!sideRail) {
            sideRail = document.createElement('div');
            sideRail.className = 'search__box-others flex flex-col gap-6 lg:gap-8';
            sideRail.classList.toggle(
                'no-products',
                !layout.querySelector('.predictive-search-result--products')
            );
            layout.appendChild(sideRail);
        }
        return sideRail;
    }

    renderCategorySearchResults(targetElement) {
        targetElement.querySelector('[data-category-search-results]')?.remove();

        const categories = this.getCategorySearchMatches(this.getQuery());
        const existingBrandSection = targetElement.querySelector(
            '.predictive-search-result--brands'
        );

        existingBrandSection?.classList.toggle(
            'predictive-search-result--brands-bordered',
            categories.length > 0
        );

        if (categories.length === 0) return false;

        const sideRail = this.ensureSearchSideRail(targetElement);

        const categorySection = this.createCategoryResultsSection(categories);
        const suggestionsSection = sideRail.querySelector('.predictive-search-result--queries');

        if (suggestionsSection) {
            suggestionsSection.after(categorySection);
        } else {
            sideRail.prepend(categorySection);
        }

        return true;
    }

    get vendorDropdown() {
        return this.querySelector('[data-search-vendor-dropdown]');
    }

    get vendorDropdownToggle() {
        return this.querySelector('[data-search-vendor-toggle]');
    }

    get vendorDropdownMenu() {
        return this.querySelector('[data-search-vendor-menu]');
    }

    get vendorDropdownOptions() {
        return [...this.querySelectorAll('[data-search-vendor-option]')];
    }

    getSectionId() {
        return this.dataset.sectionId || FoxTheme.utils.getSectionId(this);
    }

    createSubmissionQueryInput() {
        const inputName = this.input.getAttribute('name');
        if (!this.form || !inputName) return null;

        const submissionQueryInput = document.createElement('input');
        submissionQueryInput.type = 'hidden';
        submissionQueryInput.name = inputName;
        submissionQueryInput.value = this.input.value;
        submissionQueryInput.dataset.searchSubmissionQuery = '';

        this.input.removeAttribute('name');
        this.form.appendChild(submissionQueryInput);

        return submissionQueryInput;
    }

    onFocus(event) {
        this._placeholderBeforeFocus = this.input.getAttribute('placeholder');
        this.input.removeAttribute('placeholder');
        this.closeCustomVendorDropdown();
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty(
            '--header-bottom-position',
            `${parseInt(this.header.getBoundingClientRect().bottom)}px`
        );
        if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
            document.body.classList.add('search-open');
            this.toggleNavButtonVisibility(false);
        }

        const query = this.getQuery();
        if (query.length === 0) {
            this.updateEmptySearchState();
            return;
        }
        if (!this.searchRecommendationEmpty || this.hasAttribute('results')) {
            this.searchContent.classList.remove('hidden');
        } else {
            this.searchContent.classList.add('hidden');
        }
        this.classList.add('predictive-search-open');
        requestAnimationFrame(this.boundUpdateMobileSearchHeight);
        this.setInPageSearchState(!this.searchContent.classList.contains('hidden'));
        const url = this.setupURL().toString();
        this.renderSection(url, event);
    }

    onBlur() {
        if (this._placeholderBeforeFocus !== null) {
            this.input.setAttribute('placeholder', this._placeholderBeforeFocus);
        }
        this._placeholderBeforeFocus = null;
    }

    getQuery() {
        return this.input.value.trim();
    }

    getVendorSearchBrand() {
        const vendor = this.searchVendors
            ? this.searchVendors.value.trim()
            : this.dataset.vendorGnbSearchBrand?.trim() || '';

        return this.resolveVendorName(vendor);
    }

    normalizeVendorGnbHandle(value) {
        const toMenuHandle = window.VendorGnb?.utils?.toMenuHandle;
        if (typeof toMenuHandle === 'function') return toMenuHandle(value);

        return value
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()
            .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
            .replace(/^-+|-+$/g, '');
    }

    resolveVendorName(value) {
        if (!value) return '';

        const exactVendor = this.availableVendors.find(
            vendor =>
                vendor.localeCompare(value, undefined, {
                    sensitivity: 'base',
                }) === 0
        );
        if (exactVendor) return exactVendor;

        const vendorHandle = this.normalizeVendorGnbHandle(value);
        return (
            this.availableVendors.find(
                vendor => this.normalizeVendorGnbHandle(vendor) === vendorHandle
            ) || value
        );
    }

    getConfiguredVendorGnbBrand(vendor) {
        if (!vendor) return '';

        const vendorHandle = this.normalizeVendorGnbHandle(vendor);
        const vendorMenuTemplate = [
            ...document.querySelectorAll('[data-vendor-menu-template]'),
        ].find(
            template =>
                this.normalizeVendorGnbHandle(template.dataset.menuHandle || '') === vendorHandle ||
                this.normalizeVendorGnbHandle(template.dataset.menuTitle || '') === vendorHandle
        );

        return vendorMenuTemplate?.dataset.menuHandle?.trim() || '';
    }

    updateSubmissionBrand() {
        if (!this.form || !this.searchVendors) return;

        const parameterName = window.VendorGnb?.constants?.parameterName || 'brand';
        let brandInput = this.form.querySelector(`input[name="${parameterName}"]`);

        if (!brandInput) {
            brandInput = document.createElement('input');
            brandInput.type = 'hidden';
            brandInput.name = parameterName;
            this.form.appendChild(brandInput);
        }

        const vendor = this.getVendorSearchBrand();
        brandInput.value = vendor
            ? this.getConfiguredVendorGnbBrand(vendor) || this.normalizeVendorGnbHandle(vendor)
            : 'off';
    }

    parseScopedVendorQuery(query) {
        const match = query.trim().match(/^vendor:(?:"([^"]+)"|([^\s]+))(?:\s+AND(?:\s+(.*))?)?$/i);

        if (!match) return null;

        return {
            query: (match[3] || '').trim(),
            vendor: (match[1] || match[2]).trim(),
        };
    }

    initializeVendorFilter() {
        if (!this.searchVendors) return;

        const currentUrl = new URL(window.location.href);
        const urlQuery = currentUrl.searchParams.get('q')?.trim() || '';
        const scopedQuery =
            this.parseScopedVendorQuery(this.getQuery()) || this.parseScopedVendorQuery(urlQuery);
        const urlBrand = currentUrl.searchParams.get('brand')?.trim() || '';
        const fallbackBrand = urlBrand.toLowerCase() === 'off' ? '' : urlBrand;
        const vendorIdentifier = scopedQuery?.vendor || fallbackBrand;

        if (!vendorIdentifier) return;

        const matchingOption = [...this.searchVendors.options].find(
            option =>
                option.value.localeCompare(vendorIdentifier, undefined, {
                    sensitivity: 'base',
                }) === 0 ||
                this.normalizeVendorGnbHandle(option.value) ===
                    this.normalizeVendorGnbHandle(vendorIdentifier)
        );

        if (!matchingOption) return;

        this.searchVendors.value = matchingOption.value;
        if (scopedQuery) this.input.value = scopedQuery.query;

        const isHandleAlias =
            matchingOption.value.localeCompare(vendorIdentifier, undefined, {
                sensitivity: 'base',
            }) !== 0;
        const needsVendorScope = !scopedQuery && fallbackBrand && this.getQuery();

        if ((isHandleAlias || needsVendorScope) && this.closest('.template-search')) {
            const displayQuery = scopedQuery?.query ?? this.getQuery();
            currentUrl.searchParams.set('q', this.getScopedQuery(displayQuery));
            window.location.replace(currentUrl.toString());
        }
    }

    initializeCustomVendorDropdown() {
        if (!this.searchVendors || !this.vendorDropdownToggle || !this.vendorDropdownMenu) return;

        this.syncCustomVendorDropdown();
        this.vendorDropdownToggle.addEventListener('click', () => {
            this.toggleCustomVendorDropdown();
        });
        this.vendorDropdownToggle.addEventListener('keydown', event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                this.openCustomVendorDropdown(event.key === 'ArrowUp' ? 'last' : 'selected');
            } else if (event.key === 'Escape') {
                this.closeCustomVendorDropdown();
            }
        });

        this.vendorDropdownOptions.forEach(option => {
            option.addEventListener('click', () => this.selectCustomVendorOption(option));
            option.addEventListener('keydown', event =>
                this.handleCustomVendorOptionKeydown(event, option)
            );
        });
    }

    syncCustomVendorDropdown() {
        if (!this.searchVendors || !this.vendorDropdownToggle) return;

        const selectedValue = this.searchVendors.value;
        const selectedOption = this.vendorDropdownOptions.find(
            option => option.dataset.vendorValue === selectedValue
        );
        const label = this.vendorDropdownToggle.querySelector('[data-search-vendor-label]');

        if (label) label.textContent = selectedOption?.textContent.trim() || 'All brands';
        this.vendorDropdownOptions.forEach(option => {
            const isSelected = option === selectedOption;
            option.classList.toggle('is-selected', isSelected);
            option.setAttribute('aria-selected', String(isSelected));
            option.tabIndex = isSelected ? 0 : -1;
        });
    }

    openCustomVendorDropdown(focusTarget = null) {
        if (!this.vendorDropdown || !this.vendorDropdownMenu || !this.vendorDropdownToggle) return;

        this._vendorDropdownHadVisibleSearchContent =
            this.searchContent && !this.searchContent.classList.contains('hidden');
        this._vendorDropdownHadEmptyResults = this.hasAttribute('empty-results');
        this.searchContent?.classList.add('hidden');
        this.setInPageEmptyState(false);
        this.vendorDropdown.classList.add('is-open');
        this.vendorDropdownMenu.hidden = false;
        this.vendorDropdownToggle.setAttribute('aria-expanded', 'true');
        this.closest('.template-search__header')?.classList.add('vendor-dropdown-open');

        if (!focusTarget) return;

        const selectedOption = this.vendorDropdownOptions.find(option =>
            option.classList.contains('is-selected')
        );
        const optionToFocus =
            focusTarget === 'last'
                ? this.vendorDropdownOptions.at(-1)
                : selectedOption || this.vendorDropdownOptions[0];
        optionToFocus?.focus();
    }

    closeCustomVendorDropdown({ restoreFocus = false, restoreSearchContent = true } = {}) {
        if (!this.vendorDropdown || !this.vendorDropdownMenu || !this.vendorDropdownToggle) return;

        this.vendorDropdown.classList.remove('is-open');
        this.vendorDropdownMenu.hidden = true;
        this.vendorDropdownToggle.setAttribute('aria-expanded', 'false');
        this.closest('.template-search__header')?.classList.remove('vendor-dropdown-open');
        if (restoreSearchContent && this._vendorDropdownHadVisibleSearchContent) {
            this.searchContent?.classList.remove('hidden');
            this.setInPageEmptyState(Boolean(this._vendorDropdownHadEmptyResults));
        }
        this._vendorDropdownHadVisibleSearchContent = false;
        this._vendorDropdownHadEmptyResults = false;
        if (restoreFocus) this.vendorDropdownToggle.focus();
    }

    toggleCustomVendorDropdown() {
        if (this.vendorDropdown?.classList.contains('is-open')) {
            this.closeCustomVendorDropdown();
        } else {
            this.openCustomVendorDropdown();
        }
    }

    selectCustomVendorOption(option) {
        if (!this.searchVendors) return;

        this.searchVendors.value = option.dataset.vendorValue || '';
        this.syncCustomVendorDropdown();
        this.searchVendors.dispatchEvent(new Event('change', { bubbles: true }));
        this.closeCustomVendorDropdown({ restoreFocus: true, restoreSearchContent: false });
    }

    handleCustomVendorOptionKeydown(event, option) {
        const currentIndex = this.vendorDropdownOptions.indexOf(option);
        let nextIndex = null;

        if (event.key === 'ArrowDown') {
            nextIndex = Math.min(currentIndex + 1, this.vendorDropdownOptions.length - 1);
        } else if (event.key === 'ArrowUp') {
            nextIndex = Math.max(currentIndex - 1, 0);
        } else if (event.key === 'Home') {
            nextIndex = 0;
        } else if (event.key === 'End') {
            nextIndex = this.vendorDropdownOptions.length - 1;
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectCustomVendorOption(option);
            return;
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.closeCustomVendorDropdown({ restoreFocus: true });
            return;
        } else if (event.key === 'Tab') {
            this.closeCustomVendorDropdown();
            return;
        } else {
            return;
        }

        event.preventDefault();
        this.vendorDropdownOptions[nextIndex]?.focus();
    }

    formatSearchFilterValue(value) {
        const searchValue = value.replace(/["\\]/g, ' ').replace(/\s+/g, ' ').trim();
        return `"${searchValue}"`;
    }

    getScopedQuery(query) {
        const vendorBrand = this.getVendorSearchBrand();

        if (!vendorBrand || /\bvendor\s*:/i.test(query)) return query;

        const vendorQuery = `vendor:${this.formatSearchFilterValue(vendorBrand)}`;
        return query.trim() ? `${vendorQuery} AND ${query}` : vendorQuery;
    }

    onSubmit() {
        const query = this.getQuery();
        const scopedQuery = this.getScopedQuery(query);

        this.classList.remove('search-cleared');
        this.updateSubmissionBrand();

        if (this.submissionQueryInput) {
            this.submissionQueryInput.value = scopedQuery;
            return;
        }

        if (!query || scopedQuery === query) return;

        this.input.value = scopedQuery;
    }

    clear(event = null) {
        event?.preventDefault();
        // 예약된 검색과 이전 응답을 무효화해 검색어 삭제 후 결과가 다시 나타나지 않게 합니다.
        this.cancelPendingRequest();
        this.input.value = '';
        this.classList.add('search-cleared');
        this.removeAttribute('results');
        this.removeAttribute('error');
        this.setInPageEmptyState(false);
        this.input.focus();
        this.updateEmptySearchState();
    }

    updateEmptySearchState() {
        this.removeAttribute('data-has-search-query');
        // 브랜드가 선택된 인페이지 검색에서는 입력 전 추천 패널을 열지 않습니다.
        const showRecommendations =
            !this.searchRecommendationEmpty &&
            (this.isHeaderSearch || !this.getVendorSearchBrand());
        this.removeAttribute('results');
        this.toggleSearchState(showRecommendations);
        // 드롭다운을 닫으며 이전 패널 표시 상태가 복원된 경우에도 현재 조건을 적용합니다.
        this.searchContent?.classList.toggle('hidden', !showRecommendations);
        this.input.setAttribute('aria-expanded', String(showRecommendations));
        if (showRecommendations) requestAnimationFrame(this.boundUpdateMobileSearchHeight);
    }

    handleProductTypeChange(evt) {
        const query = this.getQuery();
        if (query.length > 0) {
            const url = this.setupURL().toString();
            this.renderSection(url);
        }
    }

    handleVendorChange() {
        const query = this.getQuery();
        if (query.length === 0) {
            this.updateEmptySearchState();
            return;
        }
        if (query.length > 0) {
            const url = this.setupURL().toString();
            this.renderSection(url);
        }
    }

    setupURL() {
        const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
        let search_term = this.getScopedQuery(this.getQuery());
        if (this.searchProductTypes && this.searchProductTypes.value != '') {
            search_term = `product_type:${this.searchProductTypes.value} AND ${search_term}`;
        }
        return (
            url.searchParams.set('q', search_term),
            url.searchParams.set(
                'resources[type]',
                this.getVendorSearchBrand() ? 'product' : 'product,query,collection,article'
            ),
            url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
            url.searchParams.set('resources[limit_scope]', 'each'),
            url.searchParams.set('section_id', this.getSectionId()),
            url
        );
    }

    onChange() {
        if (this.getQuery().length === 0) {
            this.clear();
            return;
        }
        const url = this.setupURL().toString();
        this.renderSection(url);
    }

    // 응답 대기 중에는 기존 결과나 추천을 유지하고 검색 중 안내만 추가합니다.
    // results를 제거하면 기존 카드가 숨겨져 빈 흰 패널이 되므로 새 응답이 올 때 교체합니다.
    showPendingResults() {
        this.toggleAttribute('data-has-search-query', this.getQuery().length > 0);
        this.removeAttribute('error');
        this.searchContent?.classList.remove('hidden');
        this.classList.add(this.states.OPEN);
        this.input.setAttribute('aria-expanded', 'true');
        this.setLoadingState(true);
        requestAnimationFrame(this.boundUpdateMobileSearchHeight);
    }

    renderSection(url) {
        this.toggleAttribute('data-has-search-query', this.getQuery().length > 0);
        this.cancelPendingRequest();
        const requestId = ++this.requestSequence;
        this.regularBlogSection = null;

        if (!this.brandSearchIndex) {
            this.loadBrandSearchIndex()
                .then(() => {
                    if (this.isCurrentRequest(requestId) && this.cachedMap.has(url)) {
                        this.renderSearchResults(this.cachedMap.get(url));
                    }
                })
                .catch(error => console.error('Unable to load brand search index.', error));
        }

        this.cachedMap.has(url)
            ? this.renderSectionFromCache(url, requestId)
            : this.renderSectionFromFetch(url, requestId);
        if (!this.getVendorSearchBrand()) this.loadRegularBlogResults(url, requestId);
    }

    async loadRegularBlogResults(predictiveUrl, requestId) {
        const sourceUrl = new URL(predictiveUrl);
        const url = new URL(sourceUrl.pathname.replace(/\/suggest$/, ''), sourceUrl.origin);
        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('type', 'article');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('page', '1');
        url.searchParams.set('section_id', 'quick-search-blog');
        const unavailable = new URL(window.location.href).searchParams.get(
            'options[unavailable_products]'
        );
        if (unavailable) url.searchParams.set('options[unavailable_products]', unavailable);
        const key = url.toString();

        try {
            if (!this.blogSearchCache.has(key)) {
                const request = fetch(url, { headers: { Accept: 'text/html' } }).then(
                    async response => {
                        if (!response.ok) throw new Error(`Blog search failed: ${response.status}`);
                        const doc = new DOMParser().parseFromString(
                            await response.text(),
                            'text/html'
                        );
                        return doc.querySelector('.predictive-search-result--blog');
                    }
                );
                this.blogSearchCache.set(key, request);
            }
            const section = await this.blogSearchCache.get(key);
            if (!this.isCurrentRequest(requestId)) return;
            this.regularBlogSection = section;
            if (this.cachedMap.has(predictiveUrl)) {
                this.renderSearchResults(this.cachedMap.get(predictiveUrl));
            }
        } catch (error) {
            this.blogSearchCache.delete(key);
            if (this.isCurrentRequest(requestId))
                console.error('Unable to load blog results.', error);
        }
    }

    mergeBlogSearchResults(targetElement) {
        if (!this.regularBlogSection) return false;
        const predictiveSection = targetElement.querySelector('.predictive-search-result--blog');
        const cards = new Map();
        [predictiveSection, this.regularBlogSection].forEach(section => {
            section?.querySelectorAll('li[data-search-result-key]').forEach(card => {
                const key = card.dataset.searchResultKey;
                if (!cards.has(key)) cards.set(key, card);
            });
        });
        if (!cards.size) return false;

        const mergedSection = (predictiveSection || this.regularBlogSection).cloneNode(true);
        const heading = mergedSection.querySelector('h4');
        const list = mergedSection.querySelector('ul');
        heading.id = `predictive-search-blog-heading-${this.getSectionId()}`;
        list.id = `predictive-search-results-blog-list-${this.getSectionId()}`;
        list.setAttribute('aria-labelledby', heading.id);
        list.replaceChildren(
            ...[...cards.values()].slice(0, 2).map((card, index) => {
                const copy = card.cloneNode(true);
                copy.id = `predictive-search-option-article-${this.getSectionId()}-${index + 1}`;
                return copy;
            })
        );
        predictiveSection?.remove();
        this.ensureSearchSideRail(targetElement).appendChild(mergedSection);
        return true;
    }

    isCurrentRequest(requestId) {
        return requestId === this.requestSequence;
    }

    cancelPendingRequest() {
        // 타이머 취소와 응답 순번 갱신을 함께 처리해 닫힌 검색창이 다시 열리는 것을 막습니다.
        clearTimeout(this.searchRequestTimer);
        this.requestSequence += 1;
        this.removeAttribute('loading');
        this.resetButton?.classList.remove(this.states.LOADING);
    }

    renderSectionFromCache(url, requestId) {
        if (!this.isCurrentRequest(requestId)) return;

        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);
        this.setLoadingState(false);
    }

    renderSectionFromFetch(url, requestId) {
        this.showPendingResults();

        window
            .fetchPredictiveSearchHTML(url)
            .then(responseText => {
                this.cachedMap.set(url, responseText);
                if (!this.isCurrentRequest(requestId)) return;

                this.renderSearchResults(responseText);
            })
            .catch(error => {
                if (!this.isCurrentRequest(requestId)) return;

                console.error('Error fetching data: ', error);
                this.setAttribute('error', 'Failed to load data');
                this.searchContent?.classList.add('hidden');
                this.toggleSearchState(false);
            })
            .finally(() => {
                if (!this.isCurrentRequest(requestId)) return;

                this.setLoadingState(false);
            });
    }
    renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + this.getSectionId();
        const targetElement = document.getElementById(id);

        if (targetElement) {
            const parser = new DOMParser();
            const parsedDoc = parser.parseFromString(responseText, 'text/html');
            const contentElement = parsedDoc.getElementById(id);

            if (contentElement) {
                this.searchContent?.classList.remove('hidden');
                this.setInPageSearchState(true);
                targetElement.innerHTML = contentElement.innerHTML;
                requestAnimationFrame(this.boundUpdateMobileSearchHeight);
                // 브랜드관 검색은 상품과 기존 빈 결과 안내만 표시합니다.
                // 보조 검색 결과가 상품 없음 안내를 대체하지 않도록 합니다.
                const isVendorSearch = Boolean(this.getVendorSearchBrand());
                if (isVendorSearch) targetElement.querySelector('.search__box-others')?.remove();
                const hasBrandResults =
                    !isVendorSearch && this.renderBrandSearchResults(targetElement);
                const hasCategoryResults =
                    !isVendorSearch && this.renderCategorySearchResults(targetElement);
                const hasBlogResults =
                    !isVendorSearch && this.mergeBlogSearchResults(targetElement);
                this.setInPageEmptyState(
                    !hasBrandResults &&
                        !hasCategoryResults &&
                        !hasBlogResults &&
                        targetElement.querySelector('[data-predictive-search-empty]') !== null
                );
                this.setAttribute('results', 'true');

                if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
                    document.body.classList.add('search-open');
                    this.toggleNavButtonVisibility(true);
                    this.classList.add('predictive-search-open');
                } else {
                    this.classList.add('predictive-search-open');
                }
            } else {
                console.error(`Element with id '${id}' not found in the parsed response.`);
            }
        } else {
            console.error(`Element with id '${id}' not found in the document.`);
        }
    }

    handleClickOutside(event) {
        const target = event.target;
        if (this.vendorDropdown && !this.vendorDropdown.contains(target)) {
            this.closeCustomVendorDropdown();
        }
        const shouldClose = this.isHeaderSearch
            ? !this.contains(target) &&
              ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
                  target.classList.contains('header__search-close'))
            : !this.contains(target);

        if (shouldClose) {
            setTimeout(() => this.toggleSearchState(false));
        }
    }

    toggleSearchState(isOpen) {
        if (!isOpen) this.cancelPendingRequest();
        this.classList.toggle(this.states.OPEN, isOpen);
        this.setInPageSearchState(isOpen);
        if (!isOpen) this.closeCustomVendorDropdown();
        if (!isOpen) this.setInPageEmptyState(false);
        if (this.isHeaderSearch) {
            document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
            this.toggleNavButtonVisibility(!isOpen);
        }
        if (!isOpen && this.searchRecommendationEmpty) {
            this.searchContent?.classList.add('hidden');
        }
    }

    toggleNavButtonVisibility(isOpen) {
        this.toggleNavigationButton &&
            this.toggleNavigationButton.classList.toggle('is-show', isOpen);
    }

    setInPageEmptyState(isEmpty) {
        if (this.isHeaderSearch) return;

        this.toggleAttribute('empty-results', isEmpty);
    }

    setInPageSearchState(isOpen) {
        // The vendor GNB also sets isHeaderSearch=false after moving its search field.
        // Its outside-click/close callbacks must not clear the results-page overlay.
        if (!this.isInPageSearch) return;

        document.body.classList.toggle('in-page-search-open', isOpen);
    }

    setLoadingState(isLoading) {
        if (isLoading) {
            this.setInPageEmptyState(false);
            this.setInPageSearchState(true);
            this.setAttribute('loading', 'true');
            this.resetButton.classList.add(this.states.LOADING);
        } else {
            this.removeAttribute('loading');
            this.resetButton.classList.remove(this.states.LOADING);
        }
    }
}
if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
}
